import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import { describeMyDayFailure, listMyDayHistory } from '../writers/my-day-writers';
import type { MyDayHistoryRow } from '../database.types';

/**
 * My Day History — WD-1 (0030, D-050).
 *
 * UI → THIS HOOK → `list_my_day_history`. **The bound and the order are the
 * server's**: the last 100 events, newest first, decided by the database.
 * Nothing here sorts, slices or filters — a hook that re-ordered an audit
 * trail would be a second opinion about what happened.
 *
 * AUTHORIZATION IS RLS: `my_day_event_select_own` matches only the caller's
 * own events, and the reader refuses anonymous callers outright.
 *
 * **A FAILED READ IS NEVER REPORTED AS AN EMPTY HISTORY** — "nothing has
 * happened" and "Crewlink cannot tell you what happened" mean opposite things,
 * and the first one is the one that gets believed.
 */
export type MyDayHistoryStatus = 'loading' | 'ready' | 'failed';

export interface MyDayHistoryState {
  status: MyDayHistoryStatus;
  /** At most 100 events, in server order — newest first. */
  events: readonly MyDayHistoryRow[];
  error: string | null;
  reload: () => Promise<void>;
}

export function useMyDayHistory(): MyDayHistoryState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<MyDayHistoryStatus>(client ? 'loading' : 'failed');
  const [events, setEvents] = useState<readonly MyDayHistoryRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  /** A read can finish after the screen is gone. Same guard as every hook here. */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fetchEvents = useCallback(async (): Promise<
    { kind: 'ready'; rows: readonly MyDayHistoryRow[] } | { kind: 'failed'; message: string }
  > => {
    if (!client) return { kind: 'failed', message: 'Crewlink is not connected to a server yet.' };
    try {
      return { kind: 'ready', rows: await listMyDayHistory(client) };
    } catch (caught) {
      reportError(caught, 'myDay.history');
      return { kind: 'failed', message: describeMyDayFailure(caught) };
    }
  }, [client]);

  const apply = useCallback(
    (
      result:
        { kind: 'ready'; rows: readonly MyDayHistoryRow[] } | { kind: 'failed'; message: string },
    ) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setEvents([]);
        setError(result.message);
        setStatus('failed');
        return;
      }
      setEvents(result.rows);
      setError(null);
      setStatus('ready');
    },
    [],
  );

  /** A superseded read finishes without writing — the house generation ticket. */
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchEvents();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchEvents, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { status, events, error, reload };
}
